import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * How many dialogs currently want the app behind them inert. Ref-counted so
 * closing one of two stacked dialogs doesn't hand the background back to the
 * screen reader while the other is still open.
 */
let inertDepth = 0;

function pushInert(): void {
  const appRoot = document.getElementById('root');
  if (!appRoot) return;
  inertDepth += 1;
  if (inertDepth === 1) {
    appRoot.setAttribute('aria-hidden', 'true');
    appRoot.setAttribute('inert', '');
  }
}

function popInert(): void {
  const appRoot = document.getElementById('root');
  if (!appRoot) return;
  inertDepth = Math.max(0, inertDepth - 1);
  if (inertDepth === 0) {
    appRoot.removeAttribute('aria-hidden');
    appRoot.removeAttribute('inert');
  }
}

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  /** Buttons pinned to the bottom of the dialog. */
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Set while a mutation is in flight so the trader can't dismiss mid-write. */
  busy?: boolean;
}

const SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
} as const;

interface ViewportBox {
  top: number;
  height: number;
}

/**
 * Track the *visual* viewport rather than the layout viewport.
 *
 * When the on-screen keyboard opens, the layout viewport does not change, so a
 * `position: fixed` sheet anchored to the bottom of the screen ends up behind
 * the keyboard along with its submit button — the classic "modal you can't
 * finish" bug. `window.visualViewport` reports the region the user can actually
 * see, and pinning the overlay to that region keeps the footer above the keys.
 *
 * Returns null where the API is missing, in which case the overlay falls back
 * to filling the layout viewport.
 */
function useVisualViewport(active: boolean): ViewportBox | null {
  const [box, setBox] = useState<ViewportBox | null>(null);

  useEffect(() => {
    if (!active) return;
    const viewport = window.visualViewport;
    if (!viewport) return;

    const sync = (): void => {
      const next = { top: viewport.offsetTop, height: viewport.height };
      // iOS fires `scroll` on the visual viewport far more often than the
      // numbers actually change; bail out rather than re-render the dialog.
      setBox((current) =>
        current && current.top === next.top && current.height === next.height ? current : next,
      );
    };

    sync();
    viewport.addEventListener('resize', sync);
    viewport.addEventListener('scroll', sync);

    return () => {
      viewport.removeEventListener('resize', sync);
      viewport.removeEventListener('scroll', sync);
      setBox(null);
    };
  }, [active]);

  return box;
}

/**
 * Modal dialog: the one component allowed a shadow, because it genuinely floats.
 *
 * Implements the full keyboard contract — Escape closes, Tab is trapped inside,
 * focus lands on the first control on open and returns to the trigger on close,
 * and the page behind is inert to scrolling.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  busy = false,
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descriptionId = useId();
  const viewport = useVisualViewport(open);

  const requestClose = useCallback(() => {
    if (busy) return;
    onClose();
  }, [busy, onClose]);

  // Remember the trigger, move focus in, and put it back on unmount.
  useEffect(() => {
    if (!open) return;

    restoreFocusRef.current = document.activeElement as HTMLElement | null;

    const frame = requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const first = panel.querySelector<HTMLElement>(FOCUSABLE);
      (first ?? panel).focus();
    });

    return () => {
      cancelAnimationFrame(frame);
      restoreFocusRef.current?.focus?.();
    };
  }, [open]);

  // Escape to dismiss, Tab cycled within the panel.
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        requestClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const panel = panelRef.current;
      if (!panel) return;

      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (element) => element.offsetParent !== null || element === document.activeElement,
      );
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, requestClose]);

  // Hide the app behind the dialog from assistive tech. `aria-modal` alone is
  // only a hint — plenty of screen readers will still browse the background
  // with the virtual cursor unless it is explicitly removed from the tree.
  useEffect(() => {
    if (!open) return;
    pushInert();
    return popInert;
  }, [open]);

  // Freeze the page behind the overlay without a layout jump.
  useEffect(() => {
    if (!open) return;
    const { body } = document;
    const previousOverflow = body.style.overflow;
    const previousPadding = body.style.paddingRight;
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;

    body.style.overflow = 'hidden';
    if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;

    return () => {
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPadding;
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed left-0 right-0 z-overlay flex items-end justify-center p-0 sm:items-center sm:p-6"
      // Pinned to the visible region, not the layout viewport, so the footer
      // stays above the on-screen keyboard instead of behind it.
      style={
        viewport
          ? { top: viewport.top, height: viewport.height }
          : { top: 0, height: '100dvh' }
      }
      role="presentation"
    >
      {/* Scrim: opacity on a bare element rather than an alpha colour, so the
          token stays the single source of truth and no channel maths is needed. */}
      <div
        className="fixed inset-0 bg-inverse opacity-40 motion-safe:[animation:overlay-in_var(--dur-base)_var(--ease-out)]"
        onClick={requestClose}
        aria-hidden="true"
      />

      {/*
        A column, not a block: on a phone this is a sheet that grows to the full
        visible height and scrolls its own body, so the header stays put and the
        footer's commit button is always on screen. Short dialogs still hug
        their content and sit as a bottom sheet.
      */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={cn(
          'relative z-overlay flex max-h-full w-full flex-col bg-surface shadow-overlay outline-none',
          'rounded-t-lg border border-line sm:rounded-lg',
          SIZES[size],
        )}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-md font-semibold tracking-tight text-fg">
              {title}
            </h2>
            {description ? (
              <div id={descriptionId} className="mt-1 text-sm text-fg-secondary">
                {description}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={requestClose}
            disabled={busy}
            aria-label="Закрыть окно"
            className={cn(
              'focus-ring -mr-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center',
              'rounded-md text-fg-muted lg:-mr-1 lg:-mt-1 lg:h-8 lg:w-8',
              'transition-colors duration-fast hover:bg-sunken hover:text-fg',
              'disabled:cursor-not-allowed disabled:opacity-40',
            )}
          >
            <X size={18} strokeWidth={1.5} aria-hidden="true" />
          </button>
        </div>

        {/* `min-h-0` is what actually lets this shrink inside the flex column;
            without it the body refuses to scroll and pushes the footer out. */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">
          {children}
        </div>

        {footer ? (
          <div
            className={cn(
              'safe-sheet-bottom flex shrink-0 flex-col-reverse gap-2',
              'border-t border-line bg-sunken px-5 pt-3',
              // Full-width stacked actions on a phone: two commit buttons side
              // by side under a thumb is how the wrong one gets pressed.
              '[&>*]:w-full',
              'sm:flex-row sm:flex-wrap sm:items-center sm:justify-end sm:[&>*]:w-auto',
            )}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
